import { Vec3, vec } from '../core/vec';
import { Model } from '../core/model';
import { extrudeFace } from '../core/ops';

/**
 * Möbel-Bibliothek: fertige Camper-Möbel (Bett, Küchenblock, Dusche …), die als
 * ganze Gruppe in die Szene gestellt werden — statt sie Fläche für Fläche zu zeichnen.
 * Alle Maße in Metern; Teile sind achsparallel, Stapel überlappen leicht, damit
 * zusammentreffende Flächen nicht flackern.
 */

/** Ein quaderförmiges Teil des Möbels: Versatz + Abmessungen relativ zum Möbel-Ursprung (Ecke vorn links unten). */
export interface FurnitureBox {
  dx: number;
  dy: number;
  dz: number;
  w: number;
  d: number;
  h: number;
  color: string;
}

export interface FurnitureTemplate {
  id: string;
  name: string;
  /** Grundfläche: Länge (x) × Tiefe (y) in Metern. */
  length: number;
  depth: number;
  note: string;
  parts: FurnitureBox[];
}

const WOOD = '#b78e5e';
const WOOD_DARK = '#7a5c3e';
const WHITE = '#f2f4f6';
const MATTRESS = '#e8edf2';
const COUNTER = '#4e4a45';
const APPLIANCE = '#3c444b';
const STEEL = '#9aa7b1';
const TRAY = '#8fa9b8';
const SHOWER_WALL = '#b8ccd8';
const CUSHION = '#d97b4f';

export const FURNITURE_TEMPLATES: FurnitureTemplate[] = [
  {
    id: 'bed-cross',
    name: 'Querbett',
    length: 1.4,
    depth: 1.95,
    note: '140 × 195 × 49',
    parts: [
      { dx: 0, dy: 0, dz: 0, w: 1.4, d: 1.95, h: 0.35, color: WOOD },
      { dx: 0.02, dy: 0.02, dz: 0.345, w: 1.36, d: 1.91, h: 0.14, color: MATTRESS }
    ]
  },
  {
    id: 'bed-single',
    name: 'Einzelbett',
    length: 0.8,
    depth: 1.95,
    note: '80 × 195 × 49',
    parts: [
      { dx: 0, dy: 0, dz: 0, w: 0.8, d: 1.95, h: 0.35, color: WOOD },
      { dx: 0.02, dy: 0.02, dz: 0.345, w: 0.76, d: 1.91, h: 0.14, color: MATTRESS }
    ]
  },
  {
    id: 'kitchen',
    name: 'Küchenblock',
    length: 1.2,
    depth: 0.6,
    note: '120 × 60 × 92',
    parts: [
      { dx: 0, dy: 0, dz: 0, w: 1.2, d: 0.6, h: 0.86, color: WOOD },
      { dx: 0, dy: 0, dz: 0.855, w: 1.2, d: 0.6, h: 0.045, color: COUNTER },
      { dx: 0.06, dy: 0.09, dz: 0.895, w: 0.52, d: 0.42, h: 0.03, color: APPLIANCE },
      { dx: 0.74, dy: 0.14, dz: 0.895, w: 0.4, d: 0.32, h: 0.03, color: STEEL }
    ]
  },
  {
    id: 'shower',
    name: 'Dusche',
    length: 0.8,
    depth: 0.8,
    note: '80 × 80 × 190',
    parts: [
      { dx: 0, dy: 0, dz: 0, w: 0.8, d: 0.8, h: 0.15, color: TRAY },
      { dx: 0.78, dy: 0, dz: 0.1, w: 0.02, d: 0.8, h: 1.8, color: SHOWER_WALL },
      { dx: 0, dy: 0.78, dz: 0.1, w: 0.8, d: 0.02, h: 1.8, color: SHOWER_WALL }
    ]
  },
  {
    id: 'wardrobe',
    name: 'Kleiderschrank',
    length: 0.6,
    depth: 0.55,
    note: '60 × 55 × 120',
    parts: [{ dx: 0, dy: 0, dz: 0, w: 0.6, d: 0.55, h: 1.2, color: WOOD_DARK }]
  },
  {
    id: 'bench',
    name: 'Sitzbank',
    length: 1.2,
    depth: 0.5,
    note: '120 × 50 × 52',
    parts: [
      { dx: 0, dy: 0, dz: 0, w: 1.2, d: 0.5, h: 0.45, color: WOOD },
      { dx: 0.01, dy: 0.01, dz: 0.445, w: 1.18, d: 0.48, h: 0.075, color: CUSHION }
    ]
  },
  {
    id: 'table',
    name: 'Tisch',
    length: 0.9,
    depth: 0.6,
    note: '90 × 60 × 75',
    parts: [
      { dx: 0, dy: 0, dz: 0, w: 0.05, d: 0.05, h: 0.705, color: WOOD_DARK },
      { dx: 0.85, dy: 0, dz: 0, w: 0.05, d: 0.05, h: 0.705, color: WOOD_DARK },
      { dx: 0, dy: 0.55, dz: 0, w: 0.05, d: 0.05, h: 0.705, color: WOOD_DARK },
      { dx: 0.85, dy: 0.55, dz: 0, w: 0.05, d: 0.05, h: 0.705, color: WOOD_DARK },
      { dx: 0, dy: 0, dz: 0.705, w: 0.9, d: 0.6, h: 0.045, color: WOOD }
    ]
  },
  {
    id: 'fridge',
    name: 'Kühlschrank',
    length: 0.55,
    depth: 0.6,
    note: '55 × 60 × 70',
    parts: [
      { dx: 0, dy: 0, dz: 0, w: 0.55, d: 0.6, h: 0.7, color: WHITE },
      { dx: 0.02, dy: 0.58, dz: 0.02, w: 0.51, d: 0.03, h: 0.66, color: APPLIANCE }
    ]
  },
  {
    id: 'toilet',
    name: 'Kassettentoilette',
    length: 0.45,
    depth: 0.5,
    note: '45 × 50 × 46',
    parts: [
      { dx: 0, dy: 0, dz: 0, w: 0.45, d: 0.5, h: 0.42, color: WHITE },
      { dx: 0.03, dy: 0.03, dz: 0.415, w: 0.39, d: 0.44, h: 0.04, color: WHITE }
    ]
  }
];

export function furnitureById(id: string): FurnitureTemplate | null {
  return FURNITURE_TEMPLATES.find((t) => t.id === id) ?? null;
}

/** Teile um 90°-Schritte (gegen den Uhrzeigersinn, von oben) gedreht; Länge/Tiefe tauschen bei ungeraden Drehungen. */
export interface OrientedParts {
  parts: FurnitureBox[];
  length: number;
  depth: number;
}

export function orientParts(tpl: FurnitureTemplate, quarterTurns: number): OrientedParts {
  let parts = tpl.parts;
  let length = tpl.length;
  let depth = tpl.depth;
  for (let i = 0; i < ((quarterTurns % 4) + 4) % 4; i++) {
    // 90° CCW: (x, y) → (−y, x), um +Tiefe in x verschoben, damit der Fußabdruck bei (0, 0) bleibt
    parts = parts.map((p) => ({ ...p, dx: depth - p.dy - p.d, dy: p.dx, w: p.d, d: p.w }));
    const nl = depth;
    depth = length;
    length = nl;
  }
  return { parts, length, depth };
}

/**
 * Stellt ein Möbelstück als eine isolierte Gruppe an `origin` (Ecke des Fußabdrucks).
 * Jedes Teil ist ein geschlossener Quader (Push/Pull-Solid); der Kontext bleibt unverändert.
 */
export function placeFurniture(model: Model, tpl: FurnitureTemplate, origin: Vec3, quarterTurns = 0): number | null {
  const outerCtx = model.editContext;
  const gid = model.addGroup(outerCtx).id;
  model.groups.get(gid)!.name = tpl.name;
  model.editContext = gid;
  try {
    for (const part of orientParts(tpl, quarterTurns).parts) {
      addBox(model, origin, part);
    }
  } finally {
    model.editContext = outerCtx;
  }
  return gid;
}

function addBox(model: Model, origin: Vec3, part: FurnitureBox): void {
  const at = (dx: number, dy: number, dz: number): Vec3 =>
    vec(origin.x + part.dx + dx, origin.y + part.dy + dy, origin.z + part.dz + dz);
  const base = [at(0, 0, 0), at(part.w, 0, 0), at(part.w, part.d, 0), at(0, part.d, 0)];
  if (part.h < 1e-6 || part.w < 1e-6 || part.d < 1e-6) return;
  // Eigene Vertices (kein Verschmelzen) → Möbel bleibt auch bei Berührung ein eigener Körper
  const ring = base.map((p) => model.addVertex(p).id);
  // Randkanten des Grundrisses anlegen, sonst findet extrudeFace den oberen Ring nicht
  for (let i = 0; i < ring.length; i++) {
    model.addEdge(ring[i]!, ring[(i + 1) % ring.length]!);
  }
  const face = model.addFace(ring, [], undefined, part.color);
  if (!face) return;
  extrudeFace(model, face.id, vec(0, 0, part.h));
}

// ---------- Eigene Möbel (aus gezeichneten Gruppen) ----------

/**
 * Ein eigenes Möbelstück: belibige Geometrie (Flächenringe + lose Kanten) relativ zur
 * Ecke des umgebenden Quaders — aus einer gezeichneten Gruppe extrahiert und als
 * Vorlage wiederverwendbar. Datenformat ist JSON-tauglich (localStorage/Projektdatei).
 */
export interface CustomFurnitureData {
  id: string;
  name: string;
  /** Maße-Text für das Panel, z. B. „84 × 42 × 90“ (cm). */
  note: string;
  /** Fußabdruck + Höhe des umgebenden Quaders (m). */
  length: number;
  depth: number;
  height: number;
  /** Normalisierte Vertices: Ecke min liegt bei (0, 0, 0). */
  vertices: [number, number, number][];
  /** [äußerer Ring, Löcher, Farbe] — Indizes in vertices. */
  faces: [number[], number[][], string | undefined][];
  /** Kanten ohne Fläche (Indizes in vertices). */
  looseEdges: [number, number][];
}

/** true, wenn die Vorlage aus gezeichneter Geometrie stammt (statt Katalog-Quadern). */
export function isUserTemplate(tpl: FurnitureTemplate | CustomFurnitureData): tpl is CustomFurnitureData {
  return (tpl as CustomFurnitureData).vertices !== undefined;
}

/** Fußabdruck einer beliebigen Möbel-Vorlage (Länge × Tiefe, m). */
export function templateFootprint(tpl: FurnitureTemplate | CustomFurnitureData): { length: number; depth: number } {
  return { length: tpl.length, depth: tpl.depth };
}

/**
 * Extrahiert eine gezeichnete Gruppe als eigene Möbel-Vorlage: Geometrie wird auf die
 * min-Ecke ihres umgebenden Quaders normalisiert, Farben bleiben erhalten.
 */
export function extractCustomTemplate(model: Model, groupId: number): CustomFurnitureData | null {
  const { edges, faces } = model.entitiesInGroup(groupId);
  if (faces.length === 0) return null;

  // Vertices sammeln und normalisieren
  const vids = model.verticesOf(new Set(edges), new Set(faces));
  if (vids.size === 0) return null;
  let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (const id of vids) {
    const p = model.vertexPosition(id);
    minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); minZ = Math.min(minZ, p.z);
    maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); maxZ = Math.max(maxZ, p.z);
  }
  const index = new Map<number, number>();
  const vertices: [number, number, number][] = [];
  for (const id of vids) {
    const p = model.vertexPosition(id);
    index.set(id, vertices.length);
    vertices.push([p.x - minX, p.y - minY, p.z - minZ]);
  }
  const map = (ring: number[]): number[] => ring.map((id) => index.get(id)!);

  const faceData: [number[], number[][], string | undefined][] = faces.map((fid) => {
    const f = model.faces.get(fid)!;
    return [map(f.outer), f.holes.map((h) => map(h)), f.color];
  });
  // Lose Kanten: nicht von einer Fläche der Gruppe begrenzt
  const faceEdges = new Set<string>();
  const key = (a: number, b: number): string => (a < b ? `${a}:${b}` : `${b}:${a}`);
  for (const f of model.faces.values()) {
    if (!faces.includes(f.id)) continue;
    for (const loop of [f.outer, ...f.holes]) {
      for (let i = 0; i < loop.length; i++) faceEdges.add(key(loop[i]!, loop[(i + 1) % loop.length]!));
    }
  }
  const looseEdges: [number, number][] = [];
  for (const eid of edges) {
    const e = model.edges.get(eid)!;
    if (!faceEdges.has(key(e.a, e.b))) looseEdges.push([index.get(e.a)!, index.get(e.b)!]);
  }

  const cm = (v: number): number => Math.round(v * 100);
  return {
    id: '',
    name: model.groups.get(groupId)?.name ?? 'Eigenes Möbel',
    note: `${cm(maxX - minX)} × ${cm(maxY - minY)} × ${cm(maxZ - minZ)}`,
    length: maxX - minX,
    depth: maxY - minY,
    height: maxZ - minZ,
    vertices,
    faces: faceData,
    looseEdges
  };
}

/** Eigene Vorlage um 90°-Schritte drehen (Formel wie orientParts, auf Vertices angewandt). */
export function orientCustom(
  tpl: CustomFurnitureData,
  quarterTurns: number
): { vertices: [number, number, number][]; length: number; depth: number } {
  let vertices = tpl.vertices;
  let length = tpl.length;
  let depth = tpl.depth;
  for (let i = 0; i < ((quarterTurns % 4) + 4) % 4; i++) {
    vertices = vertices.map(([x, y, z]) => [depth - y, x, z] as [number, number, number]);
    const nl = depth;
    depth = length;
    length = nl;
  }
  return { vertices, length, depth };
}

/** Stellt ein eigenes Möbel als isolierte Gruppe an `origin` (Ecke des Fußabdrucks). */
export function placeCustomFurniture(model: Model, tpl: CustomFurnitureData, origin: Vec3, quarterTurns = 0): number | null {
  const outerCtx = model.editContext;
  const gid = model.addGroup(outerCtx).id;
  model.groups.get(gid)!.name = tpl.name;
  model.editContext = gid;
  try {
    const { vertices } = orientCustom(tpl, quarterTurns);
    const ids = vertices.map(([x, y, z]) => model.addVertex(vec(origin.x + x, origin.y + y, origin.z + z)).id);
    for (const [a, b] of tpl.looseEdges) model.addEdge(ids[a]!, ids[b]!);
    for (const [outer, holes, color] of tpl.faces) {
      // Randkanten je Ring anlegen (addEdge dedupliziert gemeinsame Kanten benachbarter Flächen)
      for (const ring of [outer, ...holes]) {
        for (let i = 0; i < ring.length; i++) model.addEdge(ids[ring[i]!]!, ids[ring[(i + 1) % ring.length]!]!);
      }
      model.addFace(outer.map((i) => ids[i]!), holes.map((h) => h.map((i) => ids[i]!)), undefined, color);
    }
  } finally {
    model.editContext = outerCtx;
  }
  return gid;
}

/** Stellt eine beliebige Möbel-Vorlage (Katalog oder eigen) als Gruppe. */
export function placeTemplate(
  model: Model,
  tpl: FurnitureTemplate | CustomFurnitureData,
  origin: Vec3,
  quarterTurns = 0
): number | null {
  return isUserTemplate(tpl)
    ? placeCustomFurniture(model, tpl, origin, quarterTurns)
    : placeFurniture(model, tpl, origin, quarterTurns);
}
